import { addSteppingStones } from "../utils/gameAvailability";
import { levelForStars } from "../utils/playerLevel";
import { createSlice } from "@reduxjs/toolkit";
import {
  getSearchParams,
} from "../Components/nonUIFuncs";

import PlayerLevelData from "../data/playerLevel.json";
import {
  NumberDataCategory,
  ParamsToFindGame,
  Language,
  NacaApiUrl,
  NacaCommunityId,
} from "../config";
import { debug } from "../utils/debug";
import { goldStarRows } from "../utils/gameScoring";
import { dropKeylessEntries, typeLikeFirestore } from "./nacaEntries";
import { getFirestoreDocs, HAS_SECONDARY } from "./firestore";

// NACA-Core is the data source when this community has a NACA identifier
// configured. KidsZone data is public/non-sensitive, so no API key is
// needed -- the community is resolved from its immutable database id, not a
// secret. Id, not slug -- slug is user-editable in NACA-Core's community
// settings, so a rename there would silently break a hardcoded slug.
// Firebase is kept as fallback and is still used for Denaakke/Benhti secondary data.
const USE_NACA = Boolean(NacaCommunityId);

// A hung NACA-Core request (server crash, exhausted DB pool, network
// partition) never resolves and never rejects on its own -- fetch() has no
// built-in timeout, so without this a stalled request would block the
// Firebase fallback indefinitely instead of failing over quickly.
const NACA_FETCH_TIMEOUT_MS = 8000;

// Map Firestore collection names to the ?sheet= param our NACA endpoint expects.
// Strip the "KZ_" prefix and lowercase.
const COLLECTION_TO_SHEET = {
  KZ_AppData:           "appdata",
  KZ_StorybookSentence: "storybooksentence",
  KZ_StorybookVocab:    "storybookvocab",
  KZ_GameDescriptions:  "gamedescriptions",
  KZ_GameFilterDataNew: "gamefilternew",
  KZ_GoldStar:          "goldstar",
  KZ_HistoryData:       "historydata",
  KZ_Needed_Data:       "neededdata",
  KZ_PopupWords:        "popupwords",
  KZ_Possession:        "possession",
  KZ_PreSchool:         "preschool",
  KZ_uiImages:          "uiimages",
  KZ_uiWords:           "uiwords",
  KZ_GameFilterData:    "gamefilter",
};

// Firebase is the fallback and Denaakke's Benhti secondary; it loads on
// first use (see ./firestore.js).

export const reduxSlice = createSlice({
  name: "reduxSlice",
  initialState: {
    value: {
      scoreData: [],
      currentLevel: 0,
      currentLevelProgress: 0,
      width: window.innerWidth,
      height: window.innerHeight,
      stats: {
        hits: 0,
        misses: 0,
        score: 0,
        roundStats: {
          hits: 0,
          misses: 0,
          score: 0,
        },
      },
      firebaseSheetsPulled: [],
      location: "",
      homeDifficulty: "",
      vocabData: [],
      numberData: [],
      b_vocabData: [],
      b_gameData: [],
      b_numberData: [],
      sortedData: [],
      gameDescriptions: [],
      // star rules: built into the app (utils/gameScoring.js), not a sheet
      goldStar: goldStarRows(),
      popupWords: [],
      uiWords: [],
      displayCanvas: false,
      counter: 0,
      difficulty: "easy",
      specificGame: {
        displayGameType: "",
        game: "",
        Key: 0,
        category: "",
      },
      screenDisplay: "",
      bottomBarStarValues: {
        currentStars: 0,
        totalStars: 1,
      },
      playerLevelNumbers: {
        playerLevel: 0,
        allTotalStars: 1,
        currentStars: 0,
      },
    },
  },
  reducers: {
    setScoreData: (state, action) => {
      state.value = {
        ...state.value,
        scoreData: action.payload,
      };
    },
    getDifficulty: (state, action) => {
      state.value = { ...state.value, difficulty: action.payload };
    },
    getEndlessLevel: (state, action) => {
      state.value = { ...state.value, currentLevel: action.payload };
    },
    getEndlessLevelProgress: (state, action) => {
      state.value = { ...state.value, currentLevelProgress: action.payload };
    },
    getSpecificGame: (state, action) => {
      state.value = { ...state.value, specificGame: action.payload };
    },
    getSortedData: (state, action) => {
      state.value = { ...state.value, sortedData: action.payload };
    },
    setScreenDisplay: (state, action) => {
      state.value = { ...state.value, screenDisplay: action.payload };
    },
    getStats: (state, action) => {
      state.value = {
        ...state.value,
        stats: {
          ...state.value.stats,
          ...action.payload,
          roundStats: {
            ...state.value.stats.roundStats,
            ...action.payload.roundStats,
          },
        },
      };
    },
    getWidth: (state, action) => {
      state.value = { ...state.value, width: action.payload };
    },
    getHeight: (state, action) => {
      state.value = { ...state.value, height: action.payload };
    },
    setPlayerLevel: (state, action) => {
      state.value = {
        ...state.value,
        playerLevelNumbers: action.payload,
      };
    },
    setFirestoreData: (state, action) => {
      let firebaseSheets = [...state.value.firebaseSheetsPulled];
      if (!firebaseSheets.includes(action.payload.nameToStore)) {
        firebaseSheets.push(action.payload.nameToStore);
      }
      state.value = {
        ...state.value,
        [action.payload.nameToStore]:
          action.payload.nameToStore === "gameData"
            ? addSteppingStones(action.payload.sortedData)
            : action.payload.sortedData,
        firebaseSheetsPulled: firebaseSheets,
      };
    },
    getEndlessCategories: (state, action) => {
      state.value = { ...state.value, endlessCategories: action.payload };
    },
    setBottomPercentageValues: (state, action) => {
      state.value = {
        ...state.value,
        bottomBarStarValues: {
          currentStars: action.payload.currentStars,
          totalStars: action.payload.totalStars,
        },
      };
    },
  },
});

export const {
  getDifficulty,
  getSpecificGame,
  getSortedData,
  setScreenDisplay,
  getStats,
  getWidth,
  getHeight,
  setFirestoreData,
  setBottomPercentageValues,
  setPlayerLevel,
  getEndlessCategories,
  getEndlessLevel,
  getEndlessLevelProgress,
  setScoreData,
} = reduxSlice.actions;

export const setDifficulty = (difficulty) => async (dispatch) => {
  dispatch(getDifficulty(difficulty));
};
export const setEndlessCategories = (categories) => async (dispatch) => {
  dispatch(getEndlessCategories(categories));
};
export const setEndlessLevel = (level) => async (dispatch) => {
  dispatch(getEndlessLevel(level));
};
export const setEndlessLevelProgress = (levelProgress) => async (dispatch) => {
  dispatch(getEndlessLevelProgress(levelProgress));
};
export const setInitialGameData =
  (
    loc,
    data,
    vocabData,
    gameType,
    singleGameData,
    isEndless,
    selectedEndlessGame,
  ) =>
  async (dispatch) => {
    let difficulty = null;
    let specificGame = {};
    let newFilteredData = [];
    let locationArr = getSearchParams(loc);
    if (isEndless) {
      specificGame = selectedEndlessGame;
      let englishArray = [];
      newFilteredData = [];
      singleGameData.forEach((d) => {
        if (!englishArray.includes(d.English)) {
          englishArray.push(d.English);
          newFilteredData.push({
            ...d,
            Key: d.homeName === undefined ? `${d.Key}Story` : d.Key,
            homeName: d.homeName || d.storyGame,
            subCat: d.subCat || d.group,
          });
        }
      });
    } else {
      difficulty = "easy";
      // specificGame = data.find(
      //   (game) =>
      //     game.category === locationArr.find((d) => d.id === "unit").value &&
      //     game.target === locationArr.find((d) => d.id === "game-name").value &&
      //     game.displayGameType === locationArr.find((d) => d.id === "game-type").value
      // );
      let filteredGames = [];
      let KeyParam = locationArr.find((k) => k.id === "Key");
      let gameTypeParam = locationArr.find((k) => k.id === "game-type");
      if (KeyParam !== undefined && KeyParam.value.length > 0) {
        // filteredGames = data.filter((d) => Number(d.Key) === Number(KeyParam.value));
        filteredGames = data.filter(
          (d) => `${d.Key}${gameTypeParam.value}` === KeyParam.value,
        );
      } else {
        ParamsToFindGame.forEach((p) => {
          let arrayToFilter = filteredGames.length > 0 ? filteredGames : data;
          let param = locationArr.find((d) => d.id === p.param);
          let value = param.value;
          let dataToCheck = p.data;
          if (param.id === "game-type") {
            value = value.replaceAll(" ", "").replaceAll("-", "");
            dataToCheck = "game";
          }
          if (dataToCheck === "target" && value === "Story") {
            // TODO: Skip target param in story data
          } else {
            arrayToFilter = arrayToFilter.filter(
              (d) => d[dataToCheck] === value,
            );
          }
          filteredGames = arrayToFilter;
        });
      }

      specificGame =
        filteredGames[0] !== undefined ? filteredGames[0] : filteredGames;
      newFilteredData = singleGameData;
    }
    dispatch(getDifficulty(difficulty));
    dispatch(getSpecificGame(specificGame));
    dispatch(getSortedData(newFilteredData));
    dispatch(setScreenDisplay("startScreen"));
  };

const bestLevelKey = `KZ_${Language}_BestLevel`;

export const readBestLevel = () => localStorage.getItem(bestLevelKey);

const setLevel = (dispatch, { currentStars, totalStars, bestLevel }) => {
  const playerLevel = Math.max(levelForStars(currentStars, totalStars, PlayerLevelData), bestLevel);
  localStorage.setItem(bestLevelKey, String(playerLevel));
  dispatch(
    setPlayerLevel({
      playerLevel,
      bestLevel: playerLevel,
      allTotalStars: totalStars,
      currentStars,
    }),
  );
};

export const findPlayerLevel =
  (newStarNum, currentPlayerLevelNumbersState) => async (dispatch) => {
    // Passing only the difference between last and new number of stars for the
    // current game and difficulty. The player can never lose stars, only gain.
    setLevel(dispatch, {
      currentStars: currentPlayerLevelNumbersState.currentStars + newStarNum,
      totalStars: currentPlayerLevelNumbersState.allTotalStars,
      bestLevel: currentPlayerLevelNumbersState.bestLevel ?? 0,
    });
  };

export const findInitialPlayerLevelOnLoad =
  (currentStars, totalStars, bestLevel = 0) =>
  async (dispatch) => {
    setLevel(dispatch, { currentStars, totalStars, bestLevel });
  };
export const setStats = (stats) => async (dispatch) => {
  // Expected data format: { hits: 4, misses: 1, score: 3, time: 15 }
  dispatch(getStats(stats));
};
export const clearStats = () => async (dispatch) => {
  dispatch(
    getStats({
      hits: 0,
      misses: 0,
      score: 0,
      roundStats: {
        hits: 0,
        misses: 0,
        score: 0,
      },
    }),
  );
};
export const setWidth = (width) => (dispatch) => {
  dispatch(getWidth(width));
};
export const setHeight = (height) => (dispatch) => {
  dispatch(getHeight(height));
};

export const getData = (collectionName, nameToStore) => async (dispatch) => {
  let docs;
  let source = "firebase";

  if (USE_NACA) {
    const sheet = COLLECTION_TO_SHEET[collectionName];
    if (!sheet) {
      debug.warn(`[NACA] No sheet mapping for collection: ${collectionName}. Falling back to Firebase.`);
    } else {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), NACA_FETCH_TIMEOUT_MS);
      try {
        const url = `${NacaApiUrl}/api/connected/kidszone/appdata?community=${NacaCommunityId}&sheet=${sheet}`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`NACA API responded ${res.status}`);
        const json = await res.json();
        // NACA returns { data: [...], subscription_required: false, ... }
        const allItems = json.data ?? json;
        // Rows with no ID come through with an empty Key -- not ready for
        // the games yet. Then restore the number/boolean types Firestore
        // gave the same cells, which the games depend on (see nacaEntries.js).
        const keyed = dropKeylessEntries(allItems);
        const items = typeLikeFirestore(keyed);
        if (items.length !== allItems.length) {
          debug.log(`[NACA] Skipped ${allItems.length - items.length} ${collectionName} rows with no ID`);
        }
        docs = items.map((item, idx) => ({
          ...item,
          firebaseID: item.firebaseID ?? item.id ?? String(idx),
          Key: item.Key !== undefined ? item.Key : idx,
        }));
        debug.log(`[NACA] Loaded ${docs.length} records for ${collectionName}`);
        if (docs.length > 0) {
          debug.log(`[NACA] Sample record from ${collectionName}:`, JSON.stringify(docs[0], null, 2));
        }
      } catch (err) {
        const reason = err.name === "AbortError" ? `timed out after ${NACA_FETCH_TIMEOUT_MS}ms` : err;
        debug.warn(`[NACA] Fetch failed for ${collectionName}, falling back to Firebase:`, reason);
        docs = null;
      } finally {
        clearTimeout(timeoutId);
      }
    }
  }

  // An empty-but-successful NACA response means this sheet hasn't been
  // reimported into NACA-Core yet, not that the community has no data --
  // fall back to Firebase so nothing goes missing while migration is in progress.
  if (docs && docs.length === 0) {
    debug.warn(`[NACA] ${collectionName} returned 0 records, falling back to Firebase.`);
    docs = null;
  }

  if (docs) {
    source = "naca-core";
  } else {
    // If Firebase fails too (offline, or its code won't download), count the
    // sheet as empty rather than leaving start-up waiting forever: empty core
    // sheets show the "couldn't load" screen (src/index.js).
    let firestoreDocs = [];
    try {
      firestoreDocs = await getFirestoreDocs(collectionName);
    } catch (err) {
      debug.error(`[Firebase] Fetch failed for ${collectionName}:`, err);
    }
    docs = firestoreDocs.map((doc, idx) => ({
      ...doc.data(),
      firebaseID: doc.id,
      Key: doc.data().Key !== undefined ? doc.data().Key : idx,
    }));
  }

  // TEMP diagnostic (unconditional, unlike debug.log -- survives prod builds
  // since it's not stripped) to verify which source is actually serving each
  // collection. Remove once NACA-Core reimport is confirmed working live.
  console.log(`[DATA SOURCE] ${collectionName}: ${source} (${docs.length} records)`);
  let filteredData = [];
  let idArray = [];
  docs.forEach((datum) => {
    if (
      !idArray.includes(datum.Key) &&
      isNaN(datum.Key) === false &&
      datum.Key.toString().length > 0
    ) {
      let newDatum = { ...datum };
      let keys = Object.keys(newDatum);
      keys.forEach((key) => {
        if (newDatum[key] === "null") {
          datum[key] = null;
        }
      });
      if (newDatum.rank === undefined) {
        newDatum.rank = 1;
      }
      filteredData.push(newDatum);
      idArray.push(newDatum.Key);
    }
  });

  let sortedData = filteredData.sort((a, b) => a.Key - b.Key);
  let cleanedData = [...sortedData];
  if (nameToStore === "vocabData") {
    // Normalise dialect fields at import time so downstream comparisons work cleanly.
    // SetID may be a Firestore path like "KZ_AppData/DS" -- extract the last segment.
    // Dialect may be XML-tagged e.g. "<DS>setsoo</DS>" -- strip the tag and uppercase.
    cleanedData = cleanedData.map((d) => {
      let newD = { ...d };
      if (newD.SetID) {
        const parts = newD.SetID.split("/");
        newD.SetID = parts[parts.length - 1];
      }
      if (newD.Dialect) {
        let dialect = newD.Dialect;
        if (dialect.includes("<")) {
          dialect = dialect.substring(dialect.indexOf("<") + 1, dialect.indexOf(">"));
        }
        dialect = dialect.toUpperCase();
        // Map legacy single-letter codes to their full dialect codes
        const dialectMap = { L: "DL", U: "DU", C: "DC" };
        newD.Dialect = dialectMap[dialect] || dialect;
      }
      return newD;
    });
  }
  if (nameToStore === "vocabData") {
    let cat =
      NumberDataCategory === undefined ? "Numbers 1" : NumberDataCategory;
    let numberData = cleanedData.filter(
      (d) => d.subCat === cat, //TODO: when there is data, change category to numberdata for Fort Belknap
    );
    dispatch(
      setFirestoreData({
        sortedData: numberData,
        nameToStore: "numberData",
      }),
    );
  }
  dispatch(
    setFirestoreData({ sortedData: cleanedData, nameToStore: nameToStore }),
  );
  if (
    HAS_SECONDARY &&
    (nameToStore === "vocabData" || nameToStore === "gameData")
  ) {
    dispatch(getBenhtiData(collectionName, `b_${nameToStore}`));
  }
};

export const getBenhtiData =
  (collectionName, nameToStore) => async (dispatch) => {
    if (!HAS_SECONDARY) return;
    const firestoreDocs = await getFirestoreDocs(collectionName, {
      secondary: true,
    });
    const docs = firestoreDocs.map((doc, idx) => {
      let newDoc = {
        ...doc.data(),
        firebaseID: doc.id,
        Key: doc.data().Key !== undefined ? doc.data().Key : idx,
      };
      return newDoc;
    });
    let filteredData = [];
    let idArray = [];
    docs.forEach((datum) => {
      if (
        !idArray.includes(datum.Key) &&
        isNaN(datum.Key) === false &&
        datum.Key.toString().length > 0
      ) {
        let newDatum = { ...datum };
        let keys = Object.keys(newDatum);
        keys.forEach((key) => {
          if (newDatum[key] === "null") {
            newDatum[key] = null;
          }
        });
        if (newDatum.rank === undefined) {
          newDatum.rank = 1;
        }
        filteredData.push(newDatum);
        idArray.push(newDatum.Key);
      }
    });
    let sortedData = filteredData.sort((a, b) => a.Key - b.Key);
    let cleanedData = [...sortedData];
    if (nameToStore === "b_vocabData") {
      let cat =
        NumberDataCategory === undefined ? "Numbers 1" : NumberDataCategory;
      let numberData = cleanedData.filter((d) => d.subCat === cat);
      dispatch(
        setFirestoreData({
          sortedData: numberData,
          nameToStore: "b_numberData",
        }),
      );
    }
    dispatch(
      setFirestoreData({ sortedData: cleanedData, nameToStore: nameToStore }),
    );
  };
export const getBottomPercentageValues =
  (currentStars, totalStars) => async (dispatch) => {
    dispatch(
      setBottomPercentageValues({
        currentStars: currentStars,
        totalStars: totalStars,
      }),
    );
  };

export const saveScoreData = (scoreData) => async (dispatch) => {
  dispatch(setScoreData(scoreData));
};

export default reduxSlice.reducer;

// Selective selectors - only subscribe to specific state changes
export const selectScoreData = (state) => state.store.value.scoreData;
export const selectCurrentLevel = (state) => state.store.value.currentLevel;
export const selectCurrentLevelProgress = (state) =>
  state.store.value.currentLevelProgress;
export const selectWidth = (state) => state.store.value.width;
export const selectHeight = (state) => state.store.value.height;
export const selectStats = (state) => state.store.value.stats;
export const selectFirebaseSheetsPulled = (state) =>
  state.store.value.firebaseSheetsPulled;
export const selectLocation = (state) => state.store.value.location;
export const selectHomeDifficulty = (state) => state.store.value.homeDifficulty;
export const selectVocabData = (state) => state.store.value.vocabData;
export const selectNumberData = (state) => state.store.value.numberData;
export const selectSortedData = (state) => state.store.value.sortedData;
export const selectGameDescriptions = (state) =>
  state.store.value.gameDescriptions;
export const selectGoldStar = (state) => state.store.value.goldStar;
export const selectPopupWords = (state) => state.store.value.popupWords;
export const selectCurrentTarget = (state) => state.store.value.currentTarget;
export const selectDifficulty = (state) => state.store.value.difficulty;
export const selectSpecificGame = (state) => state.store.value.specificGame;
export const selectLanguage = (state) => state.store.value.Language;
export const selectUnit = (state) => state.store.value.unit;
export const selectGameType = (state) => state.store.value.gameType;
export const selectCategory = (state) => state.store.value.category;
export const selectRank = (state) => state.store.value.rank;
export const selectEndlessLevel = (state) => state.store.value.endlessLevel;
export const selectEndlessLevelProgress = (state) =>
  state.store.value.endlessLevelProgress;
export const selectScreenDisplay = (state) => state.store.value.screenDisplay;
export const selectBottomBarStarValues = (state) =>
  state.store.value.bottomBarStarValues;
export const selectPlayerLevelNumbers = (state) =>
  state.store.value.playerLevelNumbers;
export const selectDisplayCanvas = (state) => state.store.value.displayCanvas;
export const selectCounter = (state) => state.store.value.counter;
export const selectUiWords = (state) => state.store.value.uiWords;
export const selectEndlessCategories = (state) =>
  state.store.value.endlessCategories;

// Dynamic Firestore data selectors (added at runtime via setFirestoreData)
export const selectGameData = (state) => state.store.value.gameData;
export const selectHistoryData = (state) => state.store.value.historyData;
export const selectStorybookSentence = (state) =>
  state.store.value.storybookSentence;
export const selectStorybookVocab = (state) => state.store.value.storybookVocab;

// Secondary Firebase data selectors (Denaakke/Benhti only)
export const selectBVocabData = (state) => state.store.value.b_vocabData;
export const selectBNumberData = (state) => state.store.value.b_numberData;
export const selectBGameData = (state) => state.store.value.b_gameData;
